import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, HeartHandshake, PackageCheck, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const STORY_IMAGE =
  "https://images.unsplash.com/photo-1648657459277-2655e01810b2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const STATS = [
  { value: "15,000+", label: "Orders delivered across India" },
  { value: "4.8/5", label: "Average customer rating" },
  { value: "24 hrs", label: "Dispatch time on every order" },
  { value: "7 days", label: "No-questions return window" },
];

const PROMISES = [
  {
    icon: PackageCheck,
    title: "Checked twice, shipped once",
    text: "Every product passes a physical quality check before it is listed, and again before it is packed. If it fails, it never reaches you.",
  },
  {
    icon: ShieldCheck,
    title: "Your money is protected",
    text: "Payments run through Shopify's encrypted checkout, and every order is covered by our 7-day replacement-or-refund promise.",
  },
  {
    icon: HeartHandshake,
    title: "Real humans, real answers",
    text: "No bots, no runaround. Our support team replies within 24 hours and stays with you until the issue is closed.",
  },
];

export default function OurStory() {
  return (
    <main data-testid="our-story-page">
      <section className="mx-auto max-w-4xl px-4 pb-12 pt-16 text-center sm:px-6 sm:pt-24">
        <Reveal>
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-neutral-400">Our Story</p>
          <h1 className="font-script text-5xl font-semibold leading-[1.1] text-neutral-900 sm:text-6xl">
            Built on a simple promise.
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-neutral-500 sm:text-base">
            Everyday Essentials exists because shopping online in India became a gamble — endless listings, doubtful
            quality, prices that made no sense. We decided to build the store we always wished existed.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal>
          <div className="img-frame overflow-hidden rounded-2xl">
            <img
              src={STORY_IMAGE}
              alt="A calm, thoughtfully arranged modern home"
              className="h-[300px] w-full object-cover sm:h-[440px]"
              loading="lazy"
            />
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
        <Reveal>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
            Small catalog. <span className="font-serif font-medium italic">High standards.</span>
          </h2>
          <div className="mt-6 space-y-5 text-sm leading-relaxed text-neutral-600 sm:text-base">
            <p>
              Instead of listing ten thousand products, we keep a few hundred — each one tested in real homes for
              durability, usefulness, and design. We work directly with verified manufacturers, so there is no
              middleman markup and no mystery about where your product comes from.
            </p>
            <p>
              We know trust is earned order by order. That is why every parcel is packed in recyclable, plastic-free
              packaging, dispatched within 24 hours, tracked live to your door, and backed by a return policy that
              does not argue with you.
            </p>
            <p>
              Today, more than 15,000 shoppers across 28 states run their homes on our essentials — and most of them
              found us because a friend would not stop recommending us.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="border-y border-neutral-200/70 bg-white">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-8 px-4 py-14 sm:grid-cols-4 sm:px-6">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.06} className="text-center">
              <p className="font-heading text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">{s.value}</p>
              <p className="mt-2 text-xs uppercase tracking-[0.14em] text-neutral-400">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <Reveal>
          <h2 className="text-center font-heading text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
            Why customers trust us
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {PROMISES.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-neutral-200 bg-white p-7" data-testid={`trust-card-${i}`}>
                <p.icon size={22} strokeWidth={1.6} className="text-neutral-900" />
                <h3 className="mt-4 font-heading text-base font-bold">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-500">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-neutral-950">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 sm:py-24">
          <Reveal>
            <p className="font-script text-4xl font-medium leading-snug text-white sm:text-5xl">
              "If we wouldn't use it every day, we don't sell it."
            </p>
            <p className="mt-6 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-neutral-400">
              <BadgeCheck size={15} className="text-emerald-500" /> The Everyday Essentials Team
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/shop"
                data-testid="story-shop-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-neutral-900 transition-colors hover:bg-neutral-200"
              >
                Shop the Collection
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="story-contact-cta"
                className="inline-flex items-center rounded-full border border-white/30 px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                Talk to Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
